import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getSpiritualFathers, getSpiritualFatherReport } from '../../services/api';
import { FiUsers, FiAlertTriangle, FiCheckSquare, FiDollarSign } from 'react-icons/fi';
import PhoneLink from '../../components/PhoneLink';

const statusColors = { good: 'pill-green', needs_attention: 'pill-yellow', critical: 'pill-red', unknown: 'pill-gray' };

const SpiritualFatherReport = () => {
  const { t } = useTranslation();
  const [fathers, setFathers] = useState([]);
  const [fatherId, setFatherId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getSpiritualFathers().then(r => {
      const list = Array.isArray(r.data) ? r.data : [];
      setFathers(list);
      if (list.length > 0) setFatherId(String(list[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!fatherId) return;
    setLoading(true);
    getSpiritualFatherReport({ father_id: fatherId })
      .then(r => setReport(r.data || null))
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [fatherId]);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.spiritualFatherReport')}</h1></div>
      </div>

      <div className="filters-row" style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Spiritual Father</label>
        <select className="filter-select" style={{ minWidth: 240 }} value={fatherId} onChange={e => setFatherId(e.target.value)}>
          <option value="">— Select leader —</option>
          {fathers.map(f => (
            <option key={f.id} value={f.id}>{f.first_name} {f.last_name} ({f.member_count} members)</option>
          ))}
        </select>
      </div>

      {loading && <div style={{ color: '#6b7280', fontSize: 13 }}>Loading...</div>}

      {!loading && report && (
        <>
          {/* Summary cards */}
          <div className="stat-cards" style={{ marginBottom: 20 }}>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}><FiUsers /></div>
              <div>
                <div className="stat-label">Assigned Members</div>
                <div className="stat-value">{report.summary.totalMembers}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}><FiCheckSquare /></div>
              <div>
                <div className="stat-label">Active (Last 30 Days)</div>
                <div className="stat-value">{report.summary.activeAttendance}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#fee2e2', color: '#dc2626' }}><FiAlertTriangle /></div>
              <div>
                <div className="stat-label">Overdue Follow-ups</div>
                <div className="stat-value" style={{ color: report.summary.overdueFollowups > 0 ? '#dc2626' : 'inherit' }}>
                  {report.summary.overdueFollowups}
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#dbeafe', color: '#2563eb' }}><FiDollarSign /></div>
              <div>
                <div className="stat-label">This Month Giving</div>
                <div className="stat-value">{parseFloat(report.summary.totalGiving).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>

          {/* Members detail table */}
          <div className="table-card">
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: 14 }}>
                {report.father.first_name} {report.father.last_name}'s Members
              </strong>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {report.members.length} members assigned
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Dependents</th>
                  <th>Attendance (30d)</th>
                  <th>Month Giving</th>
                  <th>Last Follow-up</th>
                  <th>Next Follow-up</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {report.members.length === 0 ? (
                  <tr><td colSpan={9}>
                    <div className="empty-state"><div className="empty-icon"><FiUsers /></div><p>No members assigned to this leader</p></div>
                  </td></tr>
                ) : report.members.map((m, i) => {
                  const overdue = m.next_followup && m.next_followup < today;
                  const inactive = m.recent_attendance === 0;
                  const fullName = [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(' ');
                  return (
                    <tr key={m.id} style={{ background: inactive ? '#fffbeb' : 'transparent' }}>
                      <td data-label="#">{i + 1}</td>
                      <td data-label="Name">
                        <strong>{fullName}</strong>
                        {inactive && <span className="pill pill-yellow" style={{ marginLeft: 6, fontSize: 10 }}>Inactive</span>}
                      </td>
                      <td data-label="Phone"><PhoneLink phone={m.phone} whatsapp={m.whatsapp} /></td>
                      <td data-label="Dependents">{m.dependent_count || 0}</td>
                      <td data-label="Attendance (30d)">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{
                            fontWeight: 700,
                            color: m.recent_attendance === 0 ? '#dc2626' : m.recent_attendance >= 3 ? '#16a34a' : '#d97706'
                          }}>
                            {m.recent_attendance}
                          </span>
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>sessions</span>
                        </div>
                      </td>
                      <td data-label="Month Giving" style={{ color: parseFloat(m.month_giving) > 0 ? '#16a34a' : '#9ca3af', fontWeight: 600 }}>
                        {parseFloat(m.month_giving) > 0
                          ? parseFloat(m.month_giving).toLocaleString(undefined, { minimumFractionDigits: 2 })
                          : '—'}
                      </td>
                      <td data-label="Last Follow-up">{m.last_followup || <span style={{ color: '#9ca3af' }}>Never</span>}</td>
                      <td data-label="Next Follow-up" style={{ color: overdue ? '#dc2626' : 'inherit', fontWeight: overdue ? 700 : 'normal' }}>
                        {m.next_followup || '—'}
                        {overdue && <span className="pill pill-red" style={{ marginLeft: 4, fontSize: 10 }}>Overdue</span>}
                      </td>
                      <td data-label="Status">
                        {m.last_status
                          ? <span className={`pill ${statusColors[m.last_status] || 'pill-gray'}`}>{m.last_status.replace('_', ' ')}</span>
                          : <span style={{ color: '#9ca3af', fontSize: 12 }}>No follow-up</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Inactive members highlight */}
          {report.members.filter(m => m.recent_attendance === 0).length > 0 && (
            <div className="card" style={{ marginTop: 20, border: '1px solid #fde68a', background: '#fffbeb' }}>
              <div className="card-header" style={{ color: '#92400e' }}>
                <FiAlertTriangle style={{ marginRight: 8 }} />
                Inactive Members (no attendance in last 30 days) — need follow-up
              </div>
              <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {report.members.filter(m => m.recent_attendance === 0).map(m => (
                  <div key={m.id} style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 14px', fontSize: 13 }}>
                    <strong>{[m.first_name, m.middle_name, m.last_name].filter(Boolean).join(' ')}</strong>
                    {m.phone && <span style={{ color: '#6b7280', marginLeft: 6, fontSize: 12 }}>{m.phone}</span>}
                    {m.next_followup && m.next_followup < today && (
                      <span className="pill pill-red" style={{ marginLeft: 8, fontSize: 10 }}>Follow-up Overdue</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!loading && !report && fatherId && (
        <div className="empty-state"><p>Could not load report</p></div>
      )}

      {!fatherId && !loading && (
        <div className="card">
          <div className="empty-state" style={{ padding: '60px 24px' }}>
            <div className="empty-icon"><FiUsers /></div>
            <h3>Select a spiritual leader</h3>
            <p>Choose a leader above to view their assigned members and statistics</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpiritualFatherReport;
